// 11's settings registration — the SECOND half of ORP-18893 precondition 5 ("12's Connections entry
// **+ 11's CreditSettings**"). 12's half landed in ORP-19897; this is 11's.
//
// A function, not a module side effect, for the same reason as 12's and 22's own installers:
// `resetSettingsRegistry()` clears nav entries wholesale, so an entry registered at module-eval time
// survives exactly until the first spec that resets — then silently vanishes for the rest of that
// file. A registration a test can re-run is the only shape that survives.
//
// The registrar arrives as an ARGUMENT (contract 22 §8). 11 never imports 22's registry and 22 never
// imports 11; `app/bootstrap.ts` is the only place that knows both exist.
import type { RouteRecordRaw } from 'vue-router'

import { creditSettingsRoutes } from './routes'

import type { SettingsNavEntry } from '@/features/workspace-settings'

/** The two register functions the composition root hands every settings registrant (contract 22 §8). */
export interface CreditsSettingsRegistrar {
  registerNav: (entry: SettingsNavEntry) => void
  registerRoutes: (records: RouteRecordRaw[]) => void
}

/**
 * Register 11's AI Credits pane into module 22's settings surface.
 *
 * Routes first, same ordering rule as 12: `registerSettingsChildRoutes` throws once the router is
 * sealed, so a mis-ordered composition root should fail on the route — the loud half — rather than
 * leave a nav entry pointing at a route name that never resolves.
 */
export function installCreditsSettingsNav(reg: CreditsSettingsRegistrar): void {
  reg.registerRoutes(creditSettingsRoutes)

  reg.registerNav({
    key: 'credits',
    // 11's OWN i18n namespace — contract 22 §5: 22 renders an entry's label, it never owns the copy.
    // v3 exposes this shared subscription/AI-credit pane as “Subscription”. Keep the existing
    // nav key and route name so legacy Credits links continue to resolve to the same owner.
    labelKey: 'credits.nav.subscription',
    // The catalog has no token for v3's payment-card glyph, and the vendor class cannot be named
    // here even in a comment (`scripts/check-icon-ownership.mjs` scans comments too). `coins` is
    // the owned money silhouette this feature already draws credits with
    // (`components/infeature/CreativeCostPreview.vue`); it replaces the `gear` this entry shipped
    // with, which rendered a second "settings" glyph beside 22's Basics (ORP-20208).
    icon: 'coins',
    group: 'account',
    // New Front keeps Subscription between Account Basics and Permissions inside the Account group.
    order: 20,
    routeName: 'SettingsCredits',
    // No RBAC gate, unlike Connections. v3's entry is `active: true` for everyone
    // (`useWorkspaceSettings.js:208-213`) — there is no viewer check on Credits, so adding one here
    // would be a parity regression dressed up as hardening.
    visible: () => true,
  })
}
